import { useState, useRef, useEffect, useCallback } from 'react';
import type { Comment, Member } from '../api/client';
import type { CommentAnchor } from '../api/client';
import { api } from '../api/client';
import { useAblyComments } from '../hooks/useAblyComments';
import CommentThread from './CommentThread';
import EmptyState from './EmptyState';

interface CommentSidebarProps {
  artifactId: string;
  currentUserId?: string;
  currentVersion: number;
  collapsed: boolean;
  onToggle: () => void;
  onCommentsChanged: () => void;
  /** ID of the comment to visually highlight in the sidebar and marker layer. */
  highlightedCommentId?: string | null;
  /** ID of the comment to scroll into view in the sidebar (e.g. from marker click). */
  scrollToCommentId?: string | null;
  /** Called after the highlight scroll completes so the parent can clear the ID */
  onHighlightHandled?: () => void;
  onCommentFocus?: (comment: Comment) => void;
  onCommentHover?: (comment: Comment) => void;
  onCommentLeave?: (comment: Comment) => void;
  /** Expose open comments upward for marker rendering */
  onOpenCommentsChange?: (comments: Comment[]) => void;
  refreshNonce?: number;
  members?: Member[];
  allowMentions?: boolean;
  allowResolve?: boolean;
  allowReactions?: boolean;
  enableRealtime?: boolean;
  canComment?: boolean;
  listComments?: (artifactId: string, resolved: boolean) => Promise<{ comments: Comment[] }>;
  createComment?: (args: {
    artifactId: string;
    body: string;
    versionNumber: number;
    parentId?: string;
    anchor?: CommentAnchor | null;
    mentions?: Array<{ id: string; name: string }>;
  }) => Promise<unknown>;
  resolveComment?: (artifactId: string, commentId: string) => Promise<unknown>;
  unresolveComment?: (artifactId: string, commentId: string) => Promise<unknown>;
  toggleReaction?: (artifactId: string, commentId: string, emoji: string) => Promise<unknown>;
}

type FilterTab = 'open' | 'resolved';

const defaultListComments = (artifactId: string, resolved: boolean) =>
  api.listComments(artifactId, undefined, resolved);

const defaultCreateComment = ({
  artifactId,
  body,
  versionNumber,
  parentId,
  anchor,
  mentions,
}: {
  artifactId: string;
  body: string;
  versionNumber: number;
  parentId?: string;
  anchor?: CommentAnchor | null;
  mentions?: Array<{ id: string; name: string }>;
}) => api.createComment(artifactId, body, versionNumber, parentId, anchor, mentions);

const defaultResolveComment = (artifactId: string, commentId: string) =>
  api.resolveComment(artifactId, commentId);

const defaultUnresolveComment = (artifactId: string, commentId: string) =>
  api.unresolveComment(artifactId, commentId);

const defaultToggleReaction = (artifactId: string, commentId: string, emoji: string) =>
  api.toggleReaction(artifactId, commentId, emoji);

export default function CommentSidebar({
  artifactId,
  currentUserId,
  currentVersion,
  collapsed: _collapsed,
  onToggle,
  onCommentsChanged,
  highlightedCommentId,
  scrollToCommentId,
  onHighlightHandled,
  onCommentFocus,
  onCommentHover,
  onCommentLeave,
  onOpenCommentsChange,
  refreshNonce = 0,
  // members and allowMentions are accepted for API compat but not used since root composer moved inline
  members: _members = [],
  allowMentions: _allowMentions = true,
  allowResolve = true,
  allowReactions = true,
  enableRealtime = true,
  canComment = true,
  listComments = defaultListComments,
  createComment = defaultCreateComment,
  resolveComment = defaultResolveComment,
  unresolveComment = defaultUnresolveComment,
  toggleReaction = defaultToggleReaction,
}: CommentSidebarProps) {
  const [activeTab, setActiveTab] = useState<FilterTab>('open');
  const [resolvingIds, setResolvingIds] = useState<Set<string>>(new Set());

  // Comment lists for both tabs
  const [openComments, setOpenComments] = useState<Comment[]>([]);
  const [resolvedComments, setResolvedComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);

  // Fetch comments for both tabs
  const fetchComments = useCallback(async (mode: 'initial' | 'background' = 'background') => {
    const showLoading = mode === 'initial' || !hasLoadedOnce;
    if (showLoading) {
      setLoading(true);
    }
    setError(null);
    try {
      const [openRes, resolvedRes] = await Promise.all([
        listComments(artifactId, false),
        listComments(artifactId, true),
      ]);
      setOpenComments(openRes.comments);
      setResolvedComments(resolvedRes.comments);
      setHasLoadedOnce(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load comments');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [artifactId, hasLoadedOnce, listComments]);

  useEffect(() => {
    void fetchComments('initial');
  }, [fetchComments]);

  useEffect(() => {
    if (refreshNonce <= 0) return;
    void fetchComments('background');
  }, [fetchComments, refreshNonce]);

  // Expose open comments to parent (for markers)
  useEffect(() => {
    onOpenCommentsChange?.(openComments);
  }, [openComments, onOpenCommentsChange]);

  // Subscribe to real-time comment events via Ably
  useAblyComments(enableRealtime ? artifactId : undefined, () => {
    void fetchComments('background');
  });

  const allComments = activeTab === 'open' ? openComments : resolvedComments;

  // Filter to current version only
  const filteredComments = allComments.filter((c) => c.version_number === currentVersion);

  // Thread comments (root comments only; replies come nested)
  const threadedComments = filteredComments.filter((c) => !c.parent_id);

  // Scroll to highlighted comment
  useEffect(() => {
    if (!scrollToCommentId || !listRef.current) return;
    // Switch to open tab to find the comment
    setActiveTab('open');
    // Slight delay so the DOM updates before we scroll
    const timer = setTimeout(() => {
      const el = listRef.current?.querySelector(
        `[data-comment-id="${scrollToCommentId}"]`,
      );
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      onHighlightHandled?.();
    }, 100);
    return () => clearTimeout(timer);
  }, [onHighlightHandled, scrollToCommentId]);

  const handleRefresh = () => {
    void fetchComments('background');
    onCommentsChanged();
  };

  const handleSubmitReply = async (parentId: string, body: string) => {
    try {
      await createComment({
        artifactId,
        body,
        versionNumber: currentVersion,
        parentId,
      });
      handleRefresh();
    } catch (err) {
      console.error('Failed to submit reply:', err);
    }
  };

  const handleResolve = async (commentId: string) => {
    if (!allowResolve) return;
    // Start fade-out animation
    setResolvingIds((prev) => new Set(prev).add(commentId));

    await resolveComment(artifactId, commentId);

    // After animation completes, move to resolved list
    setTimeout(() => {
      const comment = openComments.find((c) => c.id === commentId);
      if (comment) {
        setOpenComments((prev) => prev.filter((c) => c.id !== commentId));
        setResolvedComments((prev) => [
          { ...comment, resolved_at: new Date().toISOString() },
          ...prev,
        ]);
      }
      setResolvingIds((prev) => {
        const next = new Set(prev);
        next.delete(commentId);
        return next;
      });
      onCommentsChanged();
    }, 300);
  };

  const handleUnresolve = async (commentId: string) => {
    if (!allowResolve) return;
    // Optimistically move comment from resolved to open
    const comment = resolvedComments.find((c) => c.id === commentId);
    if (comment) {
      const unresolved = { ...comment, resolved_at: null, resolved_by: null };
      setResolvedComments((prev) => prev.filter((c) => c.id !== commentId));
      setOpenComments((prev) => [...prev, unresolved]);
    }
    await unresolveComment(artifactId, commentId);
    onCommentsChanged();
  };

  const handleToggleReaction = async (commentId: string, emoji: string) => {
    if (!allowReactions) return;
    // Optimistically toggle the reaction in local state
    const updateReactions = (comments: Comment[]): Comment[] =>
      comments.map((c) => {
        if (c.id !== commentId) {
          if (c.replies) return { ...c, replies: updateReactions(c.replies) };
          return c;
        }
        const existing = c.reactions.find((r) => r.emoji === emoji);
        let newReactions;
        if (existing?.reacted) {
          // Remove user's reaction
          newReactions = c.reactions
            .map((r) => r.emoji === emoji ? { ...r, count: r.count - 1, reacted: false } : r)
            .filter((r) => r.count > 0);
        } else if (existing) {
          // Add user's reaction to existing emoji
          newReactions = c.reactions.map((r) => r.emoji === emoji ? { ...r, count: r.count + 1, reacted: true } : r);
        } else {
          // New emoji reaction
          newReactions = [...c.reactions, { emoji, count: 1, users: [], reacted: true }];
        }
        return { ...c, reactions: newReactions };
      });

    setOpenComments((prev) => updateReactions(prev));
    setResolvedComments((prev) => updateReactions(prev));

    await toggleReaction(artifactId, commentId, emoji);
  };

  const totalCount = openComments.length + resolvedComments.length;

  return (
    <aside
      role="complementary"
      aria-label="Comments"
      className="w-full min-w-0 h-full flex flex-col border-l transition-all duration-[200ms]"
      style={{
        background: 'var(--rd-surface-panel)',
        borderColor: 'var(--rd-border-subtle)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-[8px] px-[16px] border-b flex-shrink-0"
        style={{ borderColor: 'var(--rd-border-subtle)', padding: '14px 16px' }}
      >
        <span
          className="text-[14px] font-semibold"
          style={{ color: 'var(--rd-text-strong)' }}
        >
          Comments
        </span>
        <span
          className="text-[13px] font-medium"
          style={{ color: 'var(--rd-text-meta)' }}
        >
          {totalCount}
        </span>
        <div style={{ flex: 1 }} />
        <button
          onClick={onToggle}
          className="w-[28px] h-[28px] flex items-center justify-center rounded-[6px] cursor-pointer border-0 bg-transparent transition-colors duration-[120ms]"
          style={{ color: 'var(--rd-text-meta)' }}
          aria-label="Collapse comments"
          title="Close comments"
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = 'var(--rd-surface-hover)')
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = 'transparent')
          }
        >
          <i className="icon icon-app-close" style={{ fontSize: 16 }} />
        </button>
      </div>

      {/* Filter tabs */}
      <div
        className="flex gap-[2px] flex-shrink-0"
        style={{ padding: '8px 16px' }}
      >
        <button
          onClick={() => setActiveTab('open')}
          title="Show open comments"
          className="text-[13px] font-medium cursor-pointer border-0 rounded-[6px] transition-colors duration-[120ms]"
          style={{
            padding: '5px 12px',
            background: activeTab === 'open' ? 'var(--rd-surface-highlight)' : 'transparent',
            color: activeTab === 'open' ? 'var(--rd-accent)' : 'var(--rd-text-meta)',
          }}
        >
          Open
        </button>
        <button
          onClick={() => setActiveTab('resolved')}
          title="Show resolved comments"
          className="text-[13px] font-medium cursor-pointer border-0 rounded-[6px] transition-colors duration-[120ms]"
          style={{
            padding: '5px 12px',
            background: activeTab === 'resolved' ? 'var(--rd-surface-highlight)' : 'transparent',
            color: activeTab === 'resolved' ? 'var(--rd-accent)' : 'var(--rd-text-meta)',
          }}
        >
          Resolved
        </button>
      </div>

      {/* Comment list */}
      <div
        ref={listRef}
        role="feed"
        aria-label="Comment threads"
        className="flex-1 overflow-y-auto"
        style={{ padding: '16px' }}
      >
        {loading ? (
          <div className="flex items-center justify-center py-[var(--sp-12)]">
            <div
              className="w-[24px] h-[24px] border-[2px] rounded-full animate-spin"
              style={{
                borderColor: 'var(--rd-border)',
                borderTopColor: 'var(--rd-text-medium)',
              }}
            />
          </div>
        ) : error ? (
          <div className="text-[13px]" style={{ color: '#EF4444' /* notification red — keep as-is */ }}>
            {error}
          </div>
        ) : threadedComments.length === 0 ? (
          <EmptyState
            icon="icon-app-feedback"
            title={
              activeTab === 'resolved'
                ? 'No resolved comments'
                : 'No comments yet'
            }
          />
        ) : (
          threadedComments.map((comment) => (
            <CommentThread
              key={comment.id}
              comment={comment}
              isOwn={comment.author.id === currentUserId}
              currentUserId={currentUserId}
              resolving={resolvingIds.has(comment.id)}
              highlighted={highlightedCommentId === comment.id}
              onResolve={handleResolve}
              onUnresolve={handleUnresolve}
              onToggleReaction={handleToggleReaction}
              onSubmitReply={handleSubmitReply}
              onFocusMarker={activeTab === 'open' ? onCommentFocus : undefined}
              onCommentHover={activeTab === 'open' ? onCommentHover : undefined}
              onCommentLeave={activeTab === 'open' ? onCommentLeave : undefined}
              allowResolve={allowResolve}
              allowReactions={allowReactions}
              canReply={canComment}
            />
          ))
        )}
      </div>

      {/* Bottom hint — replaces the old root-level composer */}
      {canComment && (
        <div
          className="px-[var(--sp-4)] py-[var(--sp-3)] border-t flex-shrink-0 text-center"
          style={{ borderColor: 'var(--rd-border-subtle)' }}
        >
          <p
            className="text-[13px] m-0"
            style={{ color: 'var(--rd-text-faint)' }}
          >
            Use the Comment tool to click a visible element
          </p>
        </div>
      )}
    </aside>
  );
}
